import { useCallback, useMemo } from 'react';

import { ApiAuthHttpResponse, useApi } from '@/shared/api';
import {
  TwoFactorQuestionPlainObject,
  useActivateBeneficiaryProfileMutation,
} from '@/shared/generated/graphql';
import { log } from '@/shared/utils/log';

type BeneficiaryLoginInputType = {
  jwt: string;
  publicKey: string;
  password: string;
  email: string;
  twoFactorQuestions: TwoFactorQuestionPlainObject[];
};

export const useBeneficiaryLogin = () => {
  const [activateBeneficiaryProfile] = useActivateBeneficiaryProfileMutation();
  const api = useApi();

  const login = useCallback(
    async ({
      jwt,
      publicKey,
      password,
      email,
      twoFactorQuestions,
    }: BeneficiaryLoginInputType): Promise<ApiAuthHttpResponse | undefined> => {
      const activatedProfile = await activateBeneficiaryProfile({
        variables: { publicKey, password, jwt, twoFactorQuestions },
      });

      if (!activatedProfile.data?.activateBeneficiaryProfile) return undefined;

      try {
        const { data: authData } = await api.login({ email, password });
        if (!authData) return undefined;
        return authData;
      } catch (e) {
        log.error('Error while login via encrypted message');
        return undefined;
      }
    },
    [activateBeneficiaryProfile, api],
  );

  return useMemo(() => ({ login }), [login]);
};
